import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ApiService from '../../services/apiService';

const PaymentSuccess = () => {
    const { currentUser, fetchUserProfile } = useAuth();
    const [searchParams] = useSearchParams();
    const [sessionId, setSessionId] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const session = searchParams.get('session_id');
        if (session) {
            setSessionId(session);
        }
        
        // Refresh user profile and subscription status
        if (currentUser) {
            refreshSubscriptionStatus();
        }
    }, [searchParams, currentUser]);

    const refreshSubscriptionStatus = async () => {
        try {
            setLoading(true);
            const token = await currentUser.getIdToken();
            const status = await ApiService.getSubscriptionStatus(currentUser.uid, token);
            setSubscriptionStatus(status);
            
            // Refresh user profile to get updated subscription data
            await fetchUserProfile();
        } catch (error) {
            console.error('Error refreshing subscription status:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Payment Successful - Nexgen Learning</title>
                <meta name="description" content="Your payment has been processed successfully. Start your learning journey now!" />
            </Helmet>
            <Header />
            <main className="min-h-screen bg-gradient-to-b from-white to-gray-50">
                <div className="pt-28 pb-20">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        {/* Success Icon */}
                        <div className="mb-8">
                            <FaCheckCircle className="w-24 h-24 text-green-500 mx-auto" />
                        </div>

                        {/* Success Message */}
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                            Payment Successful!
                        </h1>
                        <p className="text-xl text-gray-600 mb-8">
                            Thank you for your purchase. Your membership is now active and ready to use.
                        </p>

                        {/* Subscription Status */}
                        {loading ? (
                            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
                                    <span className="text-blue-700">Updating your subscription status...</span>
                                </div>
                            </div>
                        ) : subscriptionStatus && subscriptionStatus.active ? (
                            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <FaCheckCircle className="w-5 h-5 text-green-500 mr-2" />
                                    <span className="font-semibold text-green-800">Subscription Active!</span>
                                </div>
                                <p className="text-green-700 text-sm">
                                    Plan: {subscriptionStatus.planName || 'Premium Plan'}
                                </p>
                            </div>
                        ) : (
                            <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-semibold text-yellow-800">Processing Subscription</span>
                                </div>
                                <p className="text-yellow-700 text-sm">
                                    Your subscription is being processed. Please wait a moment and refresh the page.
                                </p>
                            </div>
                        )}

                        {/* Session ID (if available) */}
                        {sessionId && (
                            <div className="bg-gray-100 rounded-lg p-4 mb-8 max-w-md mx-auto">
                                <p className="text-sm text-gray-600">
                                    <strong>Session ID:</strong> {sessionId}
                                </p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                            <Link 
                                to="/dashboard"
                                className="bg-primary text-white px-8 py-3 rounded-full font-medium hover:bg-primary-dark transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                                Go to Dashboard
                                <FaArrowRight className="w-4 h-4" />
                            </Link>
                            <Link 
                                to="/membership"
                                className="border border-primary text-primary px-8 py-3 rounded-full font-medium hover:bg-primary hover:text-white transition-colors duration-200"
                            >
                                View All Plans
                            </Link>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                What's Next?
                            </h2>
                            <div className="space-y-4 text-left">
                                <div className="flex items-start space-x-3">
                                    <div className="bg-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <span className="text-primary font-bold text-sm">1</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Access Your Dashboard</h3>
                                        <p className="text-gray-600 text-sm">Go to your dashboard to start your first learning session.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="bg-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <span className="text-primary font-bold text-sm">2</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Choose Your Teacher</h3>
                                        <p className="text-gray-600 text-sm">Select from our AI teachers or create a custom one.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="bg-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <span className="text-primary font-bold text-sm">3</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Start Learning</h3>
                                        <p className="text-gray-600 text-sm">Begin your personalized learning journey with AI tutoring.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Support */}
                        <div className="mt-12">
                            <p className="text-gray-600 mb-4">
                                Need help getting started?
                            </p>
                            <Link 
                                to="/contact"
                                className="text-primary hover:text-primary-dark font-medium"
                            >
                                Contact our support team
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
};

export default PaymentSuccess;
